import {
  BackEndValueType,
  ECardinality,
  EScreenType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IProject,
  IProjectDto,
  IProjectsResponseDto,
  IScreenDesignInfo,
  ISymbol,
  ITargetScreenInputParam
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { ProjectService } from "../../../../services/project-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IPureDesignComponentProps, IPureDesignComponentState, PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { isActionParameterShownInPanel } from "./action-parameter-visibility";
import { NavigationScreenInputPicker } from "./navigation-screen-input-picker";
import { SymbolPicker } from "./symbol-picker";

export interface ISendPushNotificationSymbolsGroupState {
  isProjectScreensLoading: boolean;
  selectedProject?: IProject;
  projectList: IProjectsResponseDto[];
  screenList: IScreenDesignInfo[];
  isNavigateOnNotificationClickVisible: boolean;
}

export interface ISendPushNotificationSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  targetScreenInputParams: ITargetScreenInputParam[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  deleteTargetScreenInputParams: (parameterName: string) => void;
}

class SendPushNotificationSymbolsGroup extends PureSdComponent<
  ISendPushNotificationSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISendPushNotificationSymbolsGroupState
> {
  constructor(props: ISendPushNotificationSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isProjectScreensLoading: false,
      projectList: [],
      screenList: [],
      isNavigateOnNotificationClickVisible: true
    };
  }

  componentDidUpdate(
    prevProps: Readonly<
      IPureDesignComponentProps & IWithSDProps & ISendPushNotificationSymbolsGroupProps & IWithContextProp
    >,
    prevState: Readonly<IPureDesignComponentState & ISendPushNotificationSymbolsGroupState>,
    snapshot?: any
  ): void {
    const prevAppIdProp = prevProps.parameters.find((x) => x.name === "appId");
    const currentAppIdProp = this.props.parameters.find((x) => x.name === "appId");
    if (prevAppIdProp?.symbol.fixedValue !== currentAppIdProp?.symbol.fixedValue) {
      const appIdValue = currentAppIdProp?.symbol.fixedValue
        ? currentAppIdProp.symbol.fixedValue
        : this.props.sd.project.id;
      this.loadProjectAndScreenList(appIdValue);
      this.onSelectToScreen("");
    }
  }

  componentDidMount(): void {
    const currentAppIdProp = this.props.parameters.find((x) => x.name === "appId");

    this.loadProjects(() =>
      this.loadProjectAndScreenList(currentAppIdProp?.symbol.fixedValue || this.props.sd.project.id)
    );
  }

  loadProjects = async (callback?: () => Promise<void>) => {
    try {
      this.props.sd.setProjectsLoadingStatus(true, false);
      const response = await ProjectService.GetAll();

      if (response.status !== 200) return;

      this.props.sd.setProjects(response.data, async () => {
        if (callback) {
          await callback();
        }
        this.forceUpdate();
      });
      this.props.sd.setProjectsLoadingStatus(false, true);
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.props.sd.setProjectsLoadingStatus(false, this.props.sd.isProjectsLoaded);
    }
  };

  async getProjectFromServer(projectId: string): Promise<IProject | undefined> {
    let project: IProject | undefined;
    const projectData = (await (await ProjectService.Get(projectId)).data) as IProjectDto;
    project = KMainFunctions.convertProjectDTOToInterface(projectData);
    return project;
  }

  loadProjectAndScreenList = async (projectId: string) => {
    try {
      this.setState({ isProjectScreensLoading: true });
      let screenList = [] as IScreenDesignInfo[];
      const res = await (await ProjectService.GetScreenList(projectId)).data;
      screenList = KMainFunctions.convertScreenListDTOToInfoInterface(res);
      const selectedProject = await this.getProjectFromServer(projectId);
      this.setState({ screenList, selectedProject });
      const result = await this.props.sd.getProjectsWithWorkflowData();
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isProjectScreensLoading: false });
    }
  };

  deleteTargetScreenInputParams = (screenInputName: string) => {
    if (this.props.deleteTargetScreenInputParams) {
      this.props.deleteTargetScreenInputParams(screenInputName);
    }
  };

  getParameterWithName = (name: string): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  getToProjectSelectValue = (): string | undefined => {
    const appIdParam = this.getParameterWithName("appId");
    if (appIdParam?.symbol?.fixedValue && appIdParam.symbol.fixedValue !== "") {
      return appIdParam.symbol.fixedValue;
    }
    return undefined;
  };

  getToScreenSelectValue = () => {
    let screenIdValue = "";
    const toScreenIdParam = this.getParameterWithName("toScreenId");
    if (toScreenIdParam?.symbol?.fixedValue && toScreenIdParam.symbol.fixedValue !== "") {
      screenIdValue = toScreenIdParam.symbol.fixedValue;
    }

    return screenIdValue;
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => {
    if (this.props.updateValues !== undefined) {
      this.props.updateValues(value, changeReason);
    }
  };

  onSelectToProject = (val: string): void => {
    const parameters = [...this.props.parameters];
    const appIdParameter = parameters.find((x) => x.name === "appId");
    const toScreenIdParameter = parameters.find((x) => x.name === "toScreenId");
    if (appIdParameter) {
      if (val === appIdParameter.symbol.fixedValue) return;

      appIdParameter.symbol.fixedValue = val;
      appIdParameter.symbol.category = ESymbolCategory.fixed;
      if (toScreenIdParameter) {
        toScreenIdParameter.symbol.fixedValue = "";
        toScreenIdParameter.symbol.category = ESymbolCategory.fixed;
        toScreenIdParameter.symbol.description = "";
      }
      this.updateValues({ actionParameters: parameters, targetScreenInputParams: [] });
    }
    if (val) this.loadProjectAndScreenList(val);
  };

  onSelectDeviceType = (val: string) => {
    const parameters = [...this.props.parameters];
    const deviceTypeParameter = parameters.find((x) => x.name === "deviceType");
    if (deviceTypeParameter) {
      deviceTypeParameter.symbol.fixedValue = val;
      deviceTypeParameter.symbol.category = ESymbolCategory.fixed;
      this.updateValues({ actionParameters: parameters });
    }
  };

  getDeviceTypeSelectValue = (): string => {
    const deviceTypeParam = this.getParameterWithName("deviceType");
    return deviceTypeParam?.symbol?.fixedValue ? deviceTypeParam.symbol.fixedValue : "";
  };

  renderDeviceType = (): JSX.Element => {
    // Values match the backend EPlatformType enum names parsed in CsSystemFunctions.SendPushNotification.
    const deviceTypeOptions: KuikaSelectboxOption[] = [
      { label: "All", value: "" },
      { label: "Android", value: "Android" },
      { label: "iOS", value: "IOS" },
      { label: "Web", value: "Web" }
    ];
    return (
      <>
        <div className="kuika_add_actions_properties_name">Device Type:</div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={this.getDeviceTypeSelectValue()}
            // "All" is the empty string, which is also what the field shows while the parameter
            // holds nothing, so choosing it is a pick that changes no value -- onChange would
            // never fire and the choice could never be saved. onSelect reports it.
            onSelect={(value: string | number | null): void => {
              if (typeof value === "string") this.onSelectDeviceType(value);
            }}
            className="kuika_add_actions_inputs_size"
            options={deviceTypeOptions}
          />
        </div>
      </>
    );
  };

  onSelectToScreen = (val: string): void => {
    const parameters = [...this.props.parameters];
    const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
    if (toScreenParameter) {
      toScreenParameter.symbol.fixedValue = val;
      const targetScreenInputParams = [] as ITargetScreenInputParam[];
      const toScreenData = this.props.sd.screenList.find((x) => x.id === val);
      if (toScreenData) {
        const targetScreenInputParamsProps = _.cloneDeep(this.props.targetScreenInputParams);
        toScreenData?.publicMetaData.screenInputs?.forEach((s) => {
          if (
            !targetScreenInputParams.some(
              (x) => x.name === s.name && x.typeName === s.type && x.cardinality === s.cardinality
            )
          ) {
            const foundParam = _.cloneDeep(
              targetScreenInputParamsProps?.find(
                (y) =>
                  y.name === s.name && (y.type === s.type || y.typeName === s.type) && y.cardinality === s.cardinality
              )
            );
            if (foundParam) {
              targetScreenInputParams.push(foundParam);
            } else {
              targetScreenInputParams.push({
                name: s.name,
                typeName: s.type,
                cardinality: s.cardinality,
                symbol: {
                  description: s.name,
                  category: ESymbolCategory.fixed,
                  fixedValue: ""
                } as ISymbol
              } as ITargetScreenInputParam);
            }
          }
        });
      }
      this.updateValues({ actionParameters: parameters, targetScreenInputParams });
    }
  };

  handleNavigateOnNotificationClickVisibility = (val) => {
    this.setState({ isNavigateOnNotificationClickVisible: val });
    if (val === false) {
      const parameters = [...this.props.parameters];
      const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
      if (toScreenParameter) {
        toScreenParameter.symbol.category = ESymbolCategory.fixed;
        toScreenParameter.symbol.fixedValue = "";
        toScreenParameter.symbol.description = "";
      }
      this.updateValues({ actionParameters: parameters });
    }
  };

  renderToProjectId = (parameter: IActionParameter): JSX.Element => {
    const isLoading: boolean = this.props.sd.isProjectsLoading || this.state.isProjectScreensLoading;
    const projectOptions: KuikaSelectboxOption[] =
      this.props.sd.projects
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.map((s): KuikaSelectboxOption => ({ key: s.id, value: s.id, label: s.name })) ?? [];

    return (
      <>
        <div className="kuika_add_actions_properties_name">To Project:</div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getToProjectSelectValue() ?? null}
            placeholder="Defaults to current app"
            allowClear
            onChange={(value: string | null): void => {
              // Clearing reports twice: the empty selection arrives here, then onClear writes the
              // empty string this parameter stores. Acting on the null would save one on the way past.
              if (value === null) return;
              this.onSelectToProject(value);
            }}
            onClear={() => this.onSelectToProject("")}
            className="kuika_add_actions_inputs_size"
            disabled={isLoading}
            options={projectOptions}
          />
        </div>
      </>
    );
  };

  renderToScreenId = (parameter: IActionParameter): JSX.Element => {
    const parameters = [...this.props.parameters];
    let targetScreenId = "";
    const toScreenParameter = parameters.find((x) => x.name === "toScreenId");
    if (toScreenParameter) {
      targetScreenId = toScreenParameter.symbol.fixedValue;
    }

    const workflowScreenData = this.state.screenList.find(
      (x) =>
        x.id === targetScreenId && this.state.selectedProject?.processStepScreenIds?.find((y) => y.screenId === x.id)
    );

    let workflowStepInstanceIdParam: ITargetScreenInputParam | undefined;

    if (workflowScreenData) {
      workflowStepInstanceIdParam = this.props.targetScreenInputParams.find((x) => x.name === "workflowStepInstanceId");
      if (!workflowStepInstanceIdParam) {
        const targetScreenInputParams = [...this.props.targetScreenInputParams];
        targetScreenInputParams.push({
          name: "workflowStepInstanceId",
          typeName: "Guid",
          cardinality: ECardinality.One,
          symbol: {
            fixedValue: "",
            category: ESymbolCategory.fixed,
            label: "",
            description: ""
          }
        } as ITargetScreenInputParam);

        this.updateValues({
          targetScreenInputParams
        });
      }
    }

    const isLoading = this.props.sd.isProjectsLoading || this.state.isProjectScreensLoading;
    const screenOptions: KuikaSelectboxOption[] =
      this.state.screenList
        ?.sort((a, b) => a.name.localeCompare(b.name))
        ?.filter((x) => x.publicMetaData.screenType !== EScreenType.Report)
        ?.map((s): KuikaSelectboxOption => ({ key: s.id, value: s.id, label: s.name })) ?? [];

    return (
      <>
        {(this.state.isNavigateOnNotificationClickVisible || parameter.symbol.fixedValue) && (
          <>
            <div className="kuika_add_actions_properties_name">To Screen:</div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                searchable
                size="sm"
                style={{ width: "100%" }}
                // While the screens are being fetched the field shows nothing, and the kit reads
                // undefined as "no value passed" -- it would keep drawing the last screen instead.
                value={isLoading ? null : this.getToScreenSelectValue()}
                onChange={(value: string): void => this.onSelectToScreen(value)}
                className="kuika_add_actions_inputs_size"
                disabled={isLoading}
                options={screenOptions}
              />
            </div>
          </>
        )}

        {!isLoading &&
          parameter.symbol.fixedValue &&
          Guid.isGuid(parameter.symbol.fixedValue) &&
          (workflowScreenData ? (
            workflowStepInstanceIdParam && this.renderToStepKey(workflowStepInstanceIdParam)
          ) : (
            <NavigationScreenInputPicker
              action={this.props.action}
              deleteTargetScreenInputParams={this.deleteTargetScreenInputParams}
              targetScreenId={targetScreenId}
              targetScreenInputParams={this.props.targetScreenInputParams}
              updateTargetScreenInputParams={(targetScreenInputParams, changeReason?: ESdChangeReason) =>
                this.updateValues({ targetScreenInputParams }, changeReason)
              }
              screenList={this.state.screenList}
            />
          ))}
      </>
    );
  };

  renderToStepKey = (param: ITargetScreenInputParam): JSX.Element => {
    const type = param.type || param.typeName;
    return (
      <div key={Guid.create().toString()}>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle parameter={{ name: "Step Key", Typename: type, cardinality: param.cardinality }} />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          key={Guid.create().toString()}
          symbol={param.symbol}
          valueType={type ? (type as BackEndValueType) : (type as BackEndValueType)}
          targetCardinality={param.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
            const targetScreenInputParams = [...this.props.targetScreenInputParams];
            const stepKeyParameter = targetScreenInputParams.find((x) => x.name === param.name);
            if (stepKeyParameter) {
              stepKeyParameter.symbol = symbol;
            }
            this.updateValues({ targetScreenInputParams }, changeReason);
          }}
        />
      </div>
    );
  };

  render() {
    if (this.props.parameters.length <= 0) return null;
    const appIdParam = this.getParameterWithName("appId");
    const toScreenIdParam = this.getParameterWithName("toScreenId");
    const deviceTypeParam = this.getParameterWithName("deviceType");
    return (
      <div style={{ width: "100%" }}>
        {this.props.parameters
          ?.filter((parameter) => {
            if (parameter.name === "appId" || parameter.name === "toScreenId" || parameter.name === "deviceType")
              return false;

            return isActionParameterShownInPanel(parameter);
          })
          .map((parameter, id) => {
            return (
              <div key={id}>
                <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                  <ActionParameterTitle
                    parameter={{
                      name: parameter.name === "url" ? "To Url" : parameter.name,
                      Typename: parameter.Typename,
                      cardinality: parameter.cardinality
                    }}
                  />
                </span>
                {
                  <SymbolPicker
                    action={this.props.action}
                    changeType="onBlur"
                    symbol={parameter.symbol}
                    valueType={parameter.Typename}
                    targetCardinality={parameter.cardinality}
                    onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                      this.props.changeActionParameter(parameter, symbol, changeReason)
                    }
                    bindingParameter={parameter}
                    bindingLabel={parameter.name === "url" ? "To Url" : parameter.name}
                  ></SymbolPicker>
                }
              </div>
            );
          })}
        {deviceTypeParam && this.renderDeviceType()}
        {(appIdParam?.isExternalVisible || toScreenIdParam?.isExternalVisible) && (
          <div className="kuika_switch_property" style={{ height: 52, paddingTop: 20 }}>
            <div style={{ float: "left" }}>
              <label
                style={{
                  width: 180,
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  display: "inline-block",
                  fontSize: 11
                }}
              >
                Navigate on Notification Click
              </label>
            </div>
            {/* <div style={{ float: "right" }}>
            <Switch size="small" onChange={this.handleNavigateOnNotificationClickVisibility} checked={this.state.isNavigateOnNotificationClickVisible || (parameter.symbol.fixedValue ? true : false)} />
          </div> */}
          </div>
        )}
        {appIdParam?.isExternalVisible === true && this.renderToProjectId(appIdParam)}
        {toScreenIdParam &&
          (toScreenIdParam.isExternalVisible === true || !toScreenIdParam.isOptionalParameter) &&
          this.renderToScreenId(toScreenIdParam)}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const sendPushNotificationSymbolsGroup = withContext<ISendPushNotificationSymbolsGroupProps>(
  withSD<ISendPushNotificationSymbolsGroupProps>(SendPushNotificationSymbolsGroup)
);
export { sendPushNotificationSymbolsGroup as SendPushNotificationSymbolsGroup };
